import { useState } from "react";
import { Table, Tag, Input, Card, Tooltip } from "antd";
import { Search, Car, AlertTriangle } from "lucide-react";
import { useFleetOverview } from "../../../api-hooks/companies";
import dayjs from "dayjs";

const FUEL_COLOR: Record<string, string> = {
  petrol: "red", diesel: "default", electric: "green", hybrid: "blue",
};

function motTag(dateStr: string | null) {
  if (!dateStr) return <Tag color="default">—</Tag>;
  const d = dayjs(dateStr);
  const diff = d.diff(dayjs(), "day");
  if (diff < 0) return <Tag color="red">OVERDUE ({d.format("DD/MM/YY")})</Tag>;
  if (diff <= 30) return <Tag color="orange">{d.format("DD/MM/YY")} ({diff}d)</Tag>;
  if (diff <= 90) return <Tag color="gold">{d.format("DD/MM/YY")} ({diff}d)</Tag>;
  return <Tag color="green">{d.format("DD/MM/YY")}</Tag>;
}

export default function PlatformFleetPage() {
  const [search, setSearch] = useState("");
  const { data: vehicles = [], isLoading } = useFleetOverview();

  const filtered = vehicles.filter((v) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      v.registration?.toLowerCase().includes(q) ||
      v.company_name?.toLowerCase().includes(q) ||
      v.make?.toLowerCase().includes(q) ||
      v.model?.toLowerCase().includes(q)
    );
  });

  const overdue = vehicles.filter((v) => v.mot_due_date && dayjs(v.mot_due_date).isBefore(dayjs())).length;
  const expiringSoon = vehicles.filter((v) => {
    if (!v.mot_due_date) return false;
    const diff = dayjs(v.mot_due_date).diff(dayjs(), "day");
    return diff >= 0 && diff <= 30;
  }).length;

  const columns = [
    {
      title: "Registration",
      dataIndex: "registration",
      key: "registration",
      render: (v: string) => <span className="font-mono font-bold text-slate-800">{v}</span>,
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      render: (v: string) => <span className="text-sm font-medium text-violet-700">{v}</span>,
    },
    {
      title: "Make / Model",
      key: "make_model",
      render: (_: any, r: any) => <span className="text-sm text-slate-700">{[r.make, r.model, r.year].filter(Boolean).join(" ")}</span>,
    },
    {
      title: "Fuel",
      dataIndex: "fuel_type",
      key: "fuel_type",
      render: (v: string) => v ? <Tag color={FUEL_COLOR[v] || "default"}>{v}</Tag> : <span className="text-slate-400">—</span>,
    },
    {
      title: "MOT Due",
      dataIndex: "mot_due_date",
      key: "mot_due_date",
      render: (v: string | null) => motTag(v),
      sorter: (a: any, b: any) => {
        if (!a.mot_due_date) return 1;
        if (!b.mot_due_date) return -1;
        return dayjs(a.mot_due_date).unix() - dayjs(b.mot_due_date).unix();
      },
      defaultSortOrder: "ascend" as const,
    },
    {
      title: "Tax Due",
      dataIndex: "tax_due_date",
      key: "tax_due_date",
      render: (v: string | null) => {
        if (!v) return <span className="text-slate-400">—</span>;
        const expired = dayjs(v).isBefore(dayjs());
        return <span className={expired ? "text-red-500 font-semibold" : "text-slate-600"}>{dayjs(v).format("DD/MM/YY")}</span>;
      },
    },
    {
      title: "Insurance Due",
      dataIndex: "insurance_due_date",
      key: "insurance_due_date",
      render: (v: string | null) => {
        if (!v) return <span className="text-slate-400">—</span>;
        const expired = dayjs(v).isBefore(dayjs());
        return <span className={expired ? "text-red-500 font-semibold" : "text-slate-600"}>{dayjs(v).format("DD/MM/YY")}</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (v: any) => <Tag color={v ? "green" : "red"}>{v ? "Active" : "Inactive"}</Tag>,
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Car size={20} className="text-violet-700" />
            <h2 className="text-xl font-semibold text-slate-800">Platform Fleet Overview</h2>
          </div>
          <p className="text-slate-500 text-sm">All vehicles across every company — sorted by MOT due date</p>
        </div>
        <div className="flex gap-4">
          {overdue > 0 && (
            <Tooltip title="Vehicles with overdue MOT">
              <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                <AlertTriangle size={14} className="text-red-500" />
                <span className="text-red-600 font-semibold text-sm">{overdue} Overdue MOT</span>
              </div>
            </Tooltip>
          )}
          {expiringSoon > 0 && (
            <Tooltip title="MOT due within 30 days">
              <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2">
                <AlertTriangle size={14} className="text-orange-500" />
                <span className="text-orange-600 font-semibold text-sm">{expiringSoon} Expiring Soon</span>
              </div>
            </Tooltip>
          )}
        </div>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm mb-4">
        <Input
          prefix={<Search size={14} className="text-slate-400" />}
          placeholder="Search by registration, company, make or model..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg"
          style={{ maxWidth: 400 }}
          allowClear
        />
      </Card>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 30, showTotal: (t) => `${t} vehicles` }}
          scroll={{ x: "max-content" }}
          rowClassName={(r) => {
            if (r.mot_due_date && dayjs(r.mot_due_date).isBefore(dayjs())) return "bg-red-50";
            return "";
          }}
        />
      </Card>
    </div>
  );
}
